import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from '@mui/styles';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    TextField,
} from '@mui/material';
import {FormattedMessage, useIntl} from 'react-intl';
import classNames from 'classnames';

import {
    borderGrayColor,
    dangerColor,
    grayColor,
    infoColor,
    infoColorDisabled,
    infoColorHover,
} from 'assets/jss/main';
import searchRelayPoints from 'api/shipment/relayPoint/searchRelayPoints';
import updateRelayPoint from 'api/shipment/relayPoint/updateRelayPoint';
import RelayPointMap from './RelayPointMap';

const useStyles = makeStyles(() => ({
    paper: {
        padding: 24,
        borderRadius: 8,
        boxShadow: '0px 4px 22px 0px rgba(0, 0, 0, 0.08)',
        '& .MuiDialogContent-root': {
            padding: 0,
            marginBottom: 24,
        },
        '& .MuiDialogContent-root h2': {
            margin: 0,
            marginBottom: 8,
            color: '#333',
            fontSize: '20px',
            fontWeight: 700,
            lineHeight: '28px',
        },
        '& .MuiDialogActions-root': {
            padding: 0,
            justifyContent: 'flex-end',
        },
        '& .MuiDialogActions-root > :not(:first-of-type)': {
            marginLeft: '12px',
        },
    },
    subtitle: {
        margin: 0,
        marginBottom: 16,
        color: grayColor,
        fontSize: '14px',
    },
    searchBar: {
        display: 'flex',
        gap: 12,
        alignItems: 'flex-start',
        marginBottom: 16,
        '& .MuiTextField-root': {
            flex: 1,
        },
        '& .MuiInputBase-input': {
            padding: '12px 14px',
        },
    },
    countryField: {
        maxWidth: 90,
    },
    body: {
        display: 'flex',
        gap: 16,
        minHeight: 520,
    },
    list: {
        width: 360,
        maxHeight: 520,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        paddingRight: 4,
    },
    map: {
        flex: 1,
        minWidth: 0,
    },
    item: {
        border: `1px solid ${borderGrayColor}`,
        borderRadius: 8,
        padding: 12,
        cursor: 'pointer',
        display: 'flex',
        gap: 12,
        alignItems: 'flex-start',
        background: '#fff',
        textAlign: 'left',
        width: '100%',
        font: 'inherit',
        '&:hover': {
            borderColor: infoColor,
        },
    },
    itemSelected: {
        borderColor: dangerColor,
        boxShadow: `0 0 0 1px ${dangerColor}`,
    },
    itemIndex: {
        flexShrink: 0,
        width: 24,
        height: 24,
        borderRadius: '50%',
        background: infoColor,
        color: '#fff',
        fontSize: '12px',
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    itemIndexSelected: {
        background: dangerColor,
    },
    itemName: {
        fontWeight: 600,
        color: '#333',
        fontSize: '14px',
        display: 'flex',
        justifyContent: 'space-between',
        gap: 8,
    },
    itemAddress: {
        color: '#555',
        fontSize: '13px',
        marginTop: 2,
    },
    itemDistance: {
        color: grayColor,
        fontSize: '12px',
        whiteSpace: 'nowrap',
    },
    itemHours: {
        marginTop: 6,
        color: grayColor,
        fontSize: '11px',
        lineHeight: '15px',
    },
    currentBadge: {
        display: 'inline-block',
        marginTop: 4,
        padding: '1px 6px',
        borderRadius: 4,
        background: '#333',
        color: '#fff',
        fontSize: '11px',
    },
    centered: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: grayColor,
        fontSize: '14px',
        textAlign: 'center',
        padding: 16,
    },
    errorMessage: {
        fontSize: '14px',
        color: dangerColor,
        marginTop: 8,
    },
    actionButton: {
        padding: '12px 16px',
        color: '#333',
        fontSize: '14px',
        lineHeight: '14px',
        fontWeight: 400,
        textTransform: 'none',
        borderRadius: 8,
        border: `1px solid ${borderGrayColor}`,
        '&:hover': {
            backgroundColor: '#F1F1F1',
        },
    },
    mapSearchButton: {
        boxShadow: '0 2px 8px rgba(0,0,0,.25)',
    },
    validatedButton: {
        color: '#FFF',
        backgroundColor: infoColor,
        '&:hover': {
            backgroundColor: infoColorHover,
        },
        '&.Mui-disabled': {
            color: '#FFF',
            backgroundColor: infoColorDisabled,
        },
    },
}));

const DAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
// Mondial Relay caps a search at 30 results; ask for the maximum so the map is as complete as possible.
const RESULT_LIMIT = 30;

function formatDistance(distance) {
    if (distance === null || distance === undefined) {
        return null;
    }

    return distance >= 1000 ? `${(distance / 1000).toFixed(1)} km` : `${distance} m`;
}

function getShipmentIri(shipment) {
    if (shipment?.['@id']) {
        return shipment['@id'];
    }
    if (shipment?.id) {
        return `/shipments/${shipment.id}`;
    }

    return null;
}

/**
 * Nested dialog opened from the shipment address modal: lists the carrier relay points around a
 * location (the current delivery address by default), lets the operator pick one on the list or on
 * the map, then applies it through PUT /shipments/{id}/relay_point.
 */
export default function ModalRelayPointPicker({open, onClose, shipment, onRelayPointChanged}) {
    const classes = useStyles();
    const intl = useIntl();

    const initialSearch = useMemo(() => ({
        country: (shipment?.deliveryCountry || 'FR').toUpperCase(),
        postalCode: shipment?.deliveryPostalCode || '',
        city: shipment?.deliveryCity || '',
    }), [shipment]);

    const [search, setSearch] = useState(initialSearch);
    const [relayPoints, setRelayPoints] = useState([]);
    const [currentRelayPointId, setCurrentRelayPointId] = useState(null);
    const [canChange, setCanChange] = useState(true);
    const [selectedId, setSelectedId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    // 'form' searches re-centre the map on the results, 'map' searches keep the view the user framed.
    const [searchOrigin, setSearchOrigin] = useState('form');
    const [mapMove, setMapMove] = useState(null);
    const listRef = useRef(null);
    const itemRefs = useRef({});

    const runSearch = useCallback(async (criteria, origin = 'form') => {
        const shipmentIri = getShipmentIri(shipment);
        if (!shipmentIri) {
            return;
        }
        setLoading(true);
        setError(null);
        setSearchOrigin(origin);
        setMapMove(null);
        try {
            const result = await searchRelayPoints(shipmentIri, {...criteria, limit: RESULT_LIMIT});
            setRelayPoints(result.relayPoints ?? []);
            setCurrentRelayPointId(result.currentRelayPoint ?? null);
            setCanChange(result.canChange !== false);
        } catch (e) {
            setRelayPoints([]);
            setError(e?.message || intl.formatMessage({id: 'picking.order.show.relayPointPicker.error.search'}));
        } finally {
            setLoading(false);
        }
    }, [shipment, intl]);

    // Reset and search around the current delivery address each time the picker opens.
    useEffect(() => {
        if (!open) {
            return;
        }
        setSearch(initialSearch);
        setSelectedId(null);
        setError(null);
        runSearch(initialSearch);
    }, [open, initialSearch, runSearch]);

    const selected = relayPoints.find(relayPoint => relayPoint.id === selectedId) ?? null;

    const handleSubmitSearch = event => {
        event.preventDefault();
        runSearch(search, 'form');
    };

    const handleMapMoveEnd = useCallback(move => setMapMove(move), []);

    useEffect(() => {
        const list = listRef.current;
        const item = selectedId ? itemRefs.current[selectedId] : null;
        if (!list || !item) {
            return;
        }
        const listRect = list.getBoundingClientRect();
        const itemRect = item.getBoundingClientRect();
        if (itemRect.top < listRect.top) {
            list.scrollTo({top: list.scrollTop + itemRect.top - listRect.top, behavior: 'smooth'});
        } else if (itemRect.bottom > listRect.bottom) {
            list.scrollTo({top: list.scrollTop + itemRect.bottom - listRect.bottom, behavior: 'smooth'});
        }
    }, [selectedId]);

    const handleSearchThisArea = () => {
        if (!mapMove) {
            return;
        }
        // Coordinates only: a postal code or city would pull the carrier search away from the framed area.
        runSearch({country: search.country, latitude: mapMove.latitude, longitude: mapMove.longitude}, 'map');
    };

    const handleConfirm = async () => {
        const shipmentIri = getShipmentIri(shipment);
        if (!selected || !shipmentIri) {
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            const result = await updateRelayPoint(shipmentIri, selected.id);
            onRelayPointChanged(result.relayPoint ?? selected);
        } catch (e) {
            setError(e?.message || intl.formatMessage({id: 'picking.order.show.relayPointPicker.error.update'}));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            slotProps={{backdrop: {style: {backgroundColor: 'rgba(51, 51, 51, 0.20)'}}}}
            classes={{paper: classes.paper}}
        >
            <DialogContent>
                <h2>
                    <FormattedMessage id="picking.order.show.relayPointPicker.title" />
                </h2>
                <p className={classes.subtitle}>
                    <FormattedMessage id="picking.order.show.relayPointPicker.subtitle" />
                </p>

                <form className={classes.searchBar} onSubmit={handleSubmitSearch}>
                    <TextField
                        className={classes.countryField}
                        size="small"
                        label={intl.formatMessage({id: 'picking.order.show.relayPointPicker.search.country'})}
                        value={search.country}
                        inputProps={{maxLength: 2}}
                        onChange={event => setSearch({...search, country: event.target.value.toUpperCase()})}
                    />
                    <TextField
                        size="small"
                        label={intl.formatMessage({id: 'picking.order.show.relayPointPicker.search.postalCode'})}
                        value={search.postalCode}
                        onChange={event => setSearch({...search, postalCode: event.target.value})}
                    />
                    <TextField
                        size="small"
                        label={intl.formatMessage({id: 'picking.order.show.relayPointPicker.search.city'})}
                        value={search.city}
                        onChange={event => setSearch({...search, city: event.target.value})}
                    />
                    <Button
                        type="submit"
                        className={classNames(classes.actionButton, classes.validatedButton)}
                        disabled={loading || (!search.postalCode && !search.city)}
                    >
                        <FormattedMessage id="picking.order.show.relayPointPicker.search.submit" />
                    </Button>
                </form>

                <div className={classes.body}>
                    <div className={classes.list} ref={listRef}>
                        {loading && (
                            <div className={classes.centered}>
                                <CircularProgress size={28} />
                            </div>
                        )}
                        {!loading && relayPoints.length === 0 && (
                            <div className={classes.centered}>
                                <FormattedMessage id="picking.order.show.relayPointPicker.empty" />
                            </div>
                        )}
                        {!loading && relayPoints.map((relayPoint, index) => {
                            const isSelected = relayPoint.id === selectedId;
                            const isCurrent = relayPoint.id === currentRelayPointId;
                            const distance = formatDistance(relayPoint.distance);
                            const hours = DAY_ORDER
                                .filter(day => relayPoint.openingHours?.[day]?.length)
                                .map(day => `${intl.formatMessage({id: `picking.order.show.relayPointPicker.day.${day}`})} ${relayPoint.openingHours[day].join(', ')}`);

                            return (
                                <button
                                    type="button"
                                    key={relayPoint.id}
                                    ref={node => { itemRefs.current[relayPoint.id] = node; }}
                                    className={classNames(classes.item, {[classes.itemSelected]: isSelected})}
                                    onClick={() => setSelectedId(relayPoint.id)}
                                >
                                    <span className={classNames(classes.itemIndex, {[classes.itemIndexSelected]: isSelected})}>{index + 1}</span>
                                    <span style={{flex: 1, minWidth: 0}}>
                                        <span className={classes.itemName}>
                                            <span>{relayPoint.name}</span>
                                            {distance && <span className={classes.itemDistance}>{distance}</span>}
                                        </span>
                                        <span className={classes.itemAddress} style={{display: 'block'}}>
                                            {relayPoint.address}{relayPoint.addressMore ? `, ${relayPoint.addressMore}` : ''}
                                            <br />
                                            {relayPoint.postalCode} {relayPoint.city}
                                        </span>
                                        {isCurrent && (
                                            <span className={classes.currentBadge}>
                                                <FormattedMessage id="picking.order.show.relayPointPicker.current" />
                                            </span>
                                        )}
                                        {hours.length > 0 && (
                                            <span className={classes.itemHours} style={{display: 'block'}}>
                                                {hours.map(line => <span key={line} style={{display: 'block'}}>{line}</span>)}
                                            </span>
                                        )}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                    <div className={classes.map}>
                        {open && (
                            <RelayPointMap
                                relayPoints={relayPoints}
                                selectedId={selectedId}
                                currentRelayPointId={currentRelayPointId}
                                onSelect={relayPoint => setSelectedId(relayPoint.id)}
                                fitToResults={searchOrigin === 'form'}
                                onMoveEnd={handleMapMoveEnd}
                                overlay={mapMove && !loading ? (
                                    <Button
                                        className={classNames(classes.actionButton, classes.validatedButton, classes.mapSearchButton)}
                                        onClick={handleSearchThisArea}
                                    >
                                        <FormattedMessage id="picking.order.show.relayPointPicker.search.thisArea" />
                                    </Button>
                                ) : null}
                            />
                        )}
                    </div>
                </div>

                {!canChange && (
                    <div className={classes.errorMessage}>
                        <FormattedMessage id="picking.order.show.relayPointPicker.error.locked" />
                    </div>
                )}
                {error && (
                    <div className={classes.errorMessage}>{error}</div>
                )}
            </DialogContent>
            <DialogActions>
                <Button className={classes.actionButton} onClick={onClose} disabled={submitting}>
                    <FormattedMessage id="picking.order.show.relayPointPicker.action.cancel" />
                </Button>
                <Button
                    className={classNames(classes.actionButton, classes.validatedButton)}
                    disabled={!selected || !canChange || submitting || selected.id === currentRelayPointId}
                    onClick={handleConfirm}
                >
                    <FormattedMessage id="picking.order.show.relayPointPicker.action.confirm" />
                </Button>
            </DialogActions>
        </Dialog>
    );
}

ModalRelayPointPicker.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    shipment: PropTypes.object,
    onRelayPointChanged: PropTypes.func.isRequired,
};

ModalRelayPointPicker.defaultProps = {
    shipment: null,
};
